import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';

import { api, ApiError } from './api/client';
import { AppShell } from './components/AppShell';
import { SignIn } from './pages/SignIn';
import { AccessDenied } from './pages/AccessDenied';
import { ComplaintsList } from './pages/ComplaintsList';
import { NewComplaint } from './pages/NewComplaint';
import { ComplaintDetail } from './pages/ComplaintDetail';
import { Reports } from './pages/Reports';
import { Users } from './pages/Users';
import { EmailSettings } from './pages/EmailSettings';
import { AuditLog } from './pages/AuditLog';
import { Loading } from './components/Loading';
import { EMAIL_SECTION_ENABLED } from './featureFlags';

/**
 * Two gates, in order:
 *   1. Entra ID — has this person signed in? Only users assigned to the
 *      application in Entra can get this far.
 *   2. The API's /me — are they registered and active in this application?
 */
function AuthorisedApp() {
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
  });

  if (isLoading) return <Loading message="Checking your access…" fullscreen />;

  if (error) {
    const message =
      error instanceof ApiError && error.isAccessDenied
        ? error.message
        : 'The Complaints Manager could not verify your access. Try again shortly.';
    return <AccessDenied message={message} />;
  }

  if (!user) return <AccessDenied message="No account details were returned." />;

  return (
    <AppShell user={user}>
      <Routes>
        <Route path="/" element={<Navigate to="/complaints" replace />} />
        <Route path="/complaints" element={<ComplaintsList />} />
        <Route path="/complaints/new" element={<NewComplaint user={user} />} />
        <Route path="/complaints/:id" element={<ComplaintDetail user={user} />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route
          path="/users"
          element={user.permissions.manage_users ? <Users user={user} /> : <Navigate to="/complaints" replace />}
        />
        <Route
          path="/email"
          element={
            EMAIL_SECTION_ENABLED && user.permissions.manage_email_config ? (
              <EmailSettings />
            ) : (
              <Navigate to="/complaints" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/complaints" replace />} />
      </Routes>
    </AppShell>
  );
}

export function App() {
  return (
    <>
      <AuthenticatedTemplate>
        <AuthorisedApp />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </>
  );
}
